import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { communityAPI } from '../services/api';
import type { CommunityProfile } from '../types';

const DEFAULT_SERVICES = [
  'Employee engagement',
  'Culture & values',
  'Leadership',
  'Wellbeing',
  'DEI',
  'HR strategy',
  'Surveys & listening',
];

const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

const CommunityMe: React.FC = () => {
  const [profile, setProfile] = useState<CommunityProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [profileType, setProfileType] = useState<'company' | 'individual'>('company');
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [summary, setSummary] = useState('');
  const [about, setAbout] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [services, setServices] = useState<string[]>([]);

  const canEdit = profile?.status === 'published';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await communityAPI.getMe();
        const p: CommunityProfile = res.data;
        setProfile(p);

        setProfileType(p.profileType);
        setCompanyName(p.companyName || '');
        setDisplayName(p.displayName || '');
        setLogoDataUrl(p.logoDataUrl || '');
        setSummary(p.summary || '');
        setAbout(p.about || '');
        setCountry(p.location?.country || '');
        setCity(p.location?.city || '');
        setContactEmail(p.contactEmail || '');
        setWebsiteUrl(p.websiteUrl || '');
        setServices(Array.isArray(p.services) ? p.services : []);
      } catch (e: any) {
        setError(e.response?.data?.message || 'Failed to load your profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleService = (s: string) => {
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleLogoChange = async (file: File | null) => {
    if (!file) {
      setLogoDataUrl('');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setLogoDataUrl(dataUrl);
  };

  const canSave = useMemo(() => {
    return Boolean(displayName.trim());
  }, [displayName]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const res = await communityAPI.updateMe({
        profileType,
        companyName,
        displayName,
        logoDataUrl,
        summary,
        about,
        services,
        location: { country, city },
        contactEmail,
        websiteUrl,
      });

      setProfile(res.data);
      setSuccess('Profile updated');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 900 }}>
        My community profile
      </Typography>

      <Box sx={{ mt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {profile && !canEdit && (
          <Alert severity="info" sx={{ mt: error || success ? 2 : 0 }}>
            Your profile is currently <strong>{profile.status}</strong>. You can edit it once an admin publishes it.
          </Alert>
        )}
      </Box>

      <Box component="form" onSubmit={handleSave} sx={{ mt: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            select
            label="Profile type"
            value={profileType}
            onChange={(e) => setProfileType(e.target.value as any)}
            fullWidth
            disabled={!canEdit}
          >
            <MenuItem value="company">Company</MenuItem>
            <MenuItem value="individual">Individual</MenuItem>
          </TextField>

          <TextField
            label="Company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            fullWidth
            disabled={!canEdit}
          />

          <TextField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            disabled={!canEdit}
            required
          />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Logo
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Button variant="outlined" component="label" disabled={!canEdit || saving}>
                Upload logo
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleLogoChange(e.target.files?.[0] || null)}
                />
              </Button>
              {logoDataUrl && (
                <Button variant="text" disabled={!canEdit || saving} onClick={() => setLogoDataUrl('')}>
                  Remove
                </Button>
              )}
            </Stack>
            {logoDataUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Box component="img" src={logoDataUrl} alt="Logo preview" sx={{ width: 96, height: 96, borderRadius: 2, objectFit: 'cover' }} />
              </Box>
            )}
          </Box>

          <TextField
            label="Summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            fullWidth
            disabled={!canEdit}
          />

          <TextField
            label="About"
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            disabled={!canEdit}
          />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Services
            </Typography>
            <FormGroup>
              {DEFAULT_SERVICES.map((s) => (
                <FormControlLabel
                  key={s}
                  control={<Checkbox checked={services.includes(s)} onChange={() => toggleService(s)} />}
                  label={s}
                  disabled={!canEdit}
                />
              ))}
            </FormGroup>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Country" value={country} onChange={(e) => setCountry(e.target.value)} fullWidth disabled={!canEdit} />
            <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth disabled={!canEdit} />
          </Stack>

          <TextField label="Contact email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} fullWidth disabled={!canEdit} />
          <TextField label="Website URL" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} fullWidth disabled={!canEdit} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button type="submit" variant="contained" disabled={!canEdit || saving || !canSave}>
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};

export default CommunityMe;
